import type { Catalogue } from "../data/schema";
import { bodyName, fill, messages, type Lang } from "../i18n/index";
import { TIER_CODE } from "../sim/body-table";

const LABEL_W = 90;
const LABEL_H = 16;
const MOBILE_LABEL_W = 110;
const MOBILE_LABEL_H = 44;
const NEAR_RADII = 30;

export interface LabelInput {
  count: number;
  screen: Float32Array;
  /** TIER_CODE per body. */
  tiers: Uint8Array;
  parentIndex: Int32Array;
  radiusKm: Float32Array;
  focus: number;
  distance: Float32Array;
  parentRadius: Float32Array;
  /** Per-body visibility from the view; point-tier bodies need 1 to get a label. */
  shown: Uint8Array;
  width: number;
  height: number;
  /** Use touch-sized label spacing on coarse pointer devices in landscape too. */
  touchTargets?: boolean;
}

const order: number[] = [];
const kept: number[] = [];

/** Marks out[k] = 1 for bodies that get a label this frame. */
export function selectLabels(input: LabelInput, out: Uint8Array): void {
  const {
    count,
    screen,
    tiers,
    parentIndex,
    radiusKm,
    focus,
    distance,
    parentRadius,
    shown,
    width,
    height,
    touchTargets,
  } = input;
  order.length = 0;
  for (let k = 0; k < count; k++) {
    out[k] = 0;
    const o = 3 * k;
    if (
      shown[k] === 0 ||
      screen[o + 2] <= 0 ||
      screen[o] < 0 ||
      screen[o] > width ||
      screen[o + 1] < 0 ||
      screen[o + 1] > height
    )
      continue;
    const p = parentIndex[k];
    const wanted =
      tiers[k] === TIER_CODE.point
        ? shown[k] === 1
        : tiers[k] === TIER_CODE.textured ||
          k === focus ||
          p === focus ||
          (p >= 0 && distance[k] < NEAR_RADII * parentRadius[p]);
    if (wanted) order.push(k);
  }
  order.sort((a, b) => radiusKm[b] - radiusKm[a]);
  kept.length = 0;
  const largeTargets = width <= 600 || touchTargets;
  const labelW = largeTargets ? MOBILE_LABEL_W : LABEL_W;
  const labelH = largeTargets ? MOBILE_LABEL_H : LABEL_H;
  for (const k of order) {
    const x = screen[3 * k],
      y = screen[3 * k + 1];
    let overlaps = false;
    for (const j of kept) {
      if (Math.abs(screen[3 * j] - x) < labelW && Math.abs(screen[3 * j + 1] - y) < labelH) {
        overlaps = true;
        break;
      }
    }
    if (overlaps) continue;
    kept.push(k);
    out[k] = 1;
  }
}

export class LabelLayer {
  private readonly elements: HTMLButtonElement[];
  private hover = -1;

  constructor(
    container: HTMLElement,
    private readonly bodies: Catalogue,
    onSelect: (id: string) => void,
  ) {
    this.elements = bodies.map((b) => {
      const el = document.createElement("button");
      el.type = "button";
      el.className = "label";
      el.addEventListener("click", () => onSelect(b.id));
      el.style.display = "none";
      container.append(el);
      return el;
    });
  }

  /** Names every label and its accessible name in the language. */
  setLang(lang: Lang): void {
    const focus = messages(lang).bodies.focus;
    this.elements.forEach((el, k) => {
      const name = bodyName(lang, this.bodies[k]);
      el.textContent = name;
      el.setAttribute("aria-label", fill(focus, { name }));
    });
  }

  setHover(index: number): void {
    if (this.hover >= 0) this.elements[this.hover].classList.remove("hover");
    this.hover = index;
    if (index >= 0) this.elements[index].classList.add("hover");
  }

  update(screen: Float32Array, visible: Uint8Array, enabled: boolean): void {
    this.elements.forEach((el, k) => {
      const show = enabled && visible[k] === 1;
      if (!show) {
        if (el.style.display !== "none") el.style.display = "none";
        return;
      }
      if (el.style.display !== "block") el.style.display = "block";
      el.style.transform = `translate(${screen[3 * k] + 8}px, ${screen[3 * k + 1] - 8}px)`;
    });
  }
}
